/**
 * /well-testing/rates — the Well Testing rate card (GET /well-testing/rates,
 * fieldiq-api#1253, WT-4).
 *
 * Full width. Filters in the URL: the card (`card=default`, a customer's id,
 * or every card) and the kind. A rate reads in the company's currency format —
 * `CA$1,150.00/shift`; a third-party rate has no rate of its own and reads as
 * `At cost` or `Cost + 15%` — and an absent figure is `—`. New and edit are full pages
 * (RateDetailScreen), never a dialog.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWtCustomers, useWtMeta } from '../../../hooks/useWellTesting';
import { useWtRates } from '../../../hooks/useWtTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWtTickets } from '../../../lib/wt-tickets';
import { catalogLabel, wtDay } from '../../../lib/well-testing';
import type { WtRate, WtTicketCatalog } from '../../../types/wt-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { WT_EYEBROW, WT_INPUT, WtChip, WtChipRow, WtEmpty, WtPanel, WtRegister, type WtColumn } from '../WtBits';
import { wtRatePriceText } from './TicketBits';

/** What a rate prices beyond its kind: the crew role, the package, the unit or the equipment type, a per-volume unit. */
export function wtRateAppliesTo(r: WtRate, crewRoles?: { value: string; label: string }[]) {
  if (r.crewRole) return r.crewRoleLabel || catalogLabel(crewRoles, r.crewRole);
  if (r.packageId) return r.packageName || '—';
  if (r.equipmentId) return r.equipmentUnitNumber ? `${r.equipmentUnitNumber} · ${r.equipmentName ?? '—'}` : r.equipmentName || '—';
  if (r.equipmentTypeId) return r.equipmentTypeName || '—';
  if (r.kind === 'third_party') return r.pricingLabel || catalogLabel(undefined, r.pricing);
  return '—';
}

export default function WtRatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canWrite = canWriteWtTickets(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const card = searchParams.get('card') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const catalog = (useWtMeta().data?.data ?? null) as WtTicketCatalog | null;
  const customers = useWtCustomers().data ?? [];
  const listQuery = useWtRates({ customer_id: card || undefined, kind: kind || undefined });
  const allQuery = useWtRates();
  const rates = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: WtRate) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const today = allQuery.data?.meta.today ?? null;
  const live = (r: WtRate) => !!today && r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today);
  const filtered = !!(card || kind);

  const columns: WtColumn<WtRate>[] = [
    { key: 'code', header: 'Code', render: (r) => (<><p className="font-mono font-extrabold text-white">{r.code || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'}</p></>) },
    { key: 'card', header: 'Card', render: (r) => (r.customerId ? r.customerName || '—' : 'Default card') },
    { key: 'kind', header: 'Kind', render: (r) => r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind) },
    { key: 'applies', header: 'Applies to', render: (r) => wtRateAppliesTo(r, catalog?.crewRoles) },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => <span className="font-mono">{wtRatePriceText(r)}</span> },
    { key: 'period', header: 'Effective', render: (r) => `${wtDay(r.effectiveFrom)} – ${r.effectiveTo ? wtDay(r.effectiveTo) : 'no end'}` },
  ];

  return (
    <div data-testid="wt-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Rate Card"
        description="What the company bills — packages, units, crew, standby, mileage, third-party and other charges — on the default card or a customer's own."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'In effect today', tone: 'accent', loading: allQuery.isLoading, value: countOf(live) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Customer rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => !!r.customerId) },
        ]}
        actions={
          canWrite ? (
            <button
              type="button"
              className={DH_BAND_SECONDARY}
              onClick={() => navigate(`/well-testing/rates/new${card && card !== 'default' ? `?customerId=${card}` : ''}`)}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WtChipRow label="Filter by kind">
            <WtChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {(catalog?.priceKinds ?? []).map((k) => (
              <WtChip key={k.value} label={k.label} active={kind === k.value} onClick={() => setParam('kind', k.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.kind === k.value)} />
            ))}
          </WtChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WT_INPUT} aria-label="Filter by card" value={card} onChange={(e) => setParam('card', e.target.value)}>
              <option value="">Every card</option>
              <option value="default">Default card</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WtPanel eyebrow="Rate card" title="Rates" testId="wt-rate-register" footer={!listQuery.isLoading && rates.length ? `${rates.length} rate${rates.length === 1 ? '' : 's'}` : undefined}>
            <WtRegister
              query={listQuery}
              rows={rates}
              columns={columns}
              rail={(r) => (live(r) ? 'border-l-fi-green' : 'border-l-fi-steel')}
              onOpen={(r) => openRecord(`/well-testing/rates/${r.id}`)}
              subject="rates"
              testId="wt-rate"
              minWidth="min-w-[920px]"
              empty={filtered ? <WtEmpty>No rate matches these filters.</WtEmpty> : <WtEmpty>No rates yet. Add the first one to price field tickets.</WtEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.code || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'} · {r.customerId ? r.customerName || '—' : 'Default card'}</p>
                    </div>
                    <span className="font-mono text-sm text-white">{wtRatePriceText(r)}</span>
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} · {wtRateAppliesTo(r, catalog?.crewRoles)} · from {wtDay(r.effectiveFrom)}
                  </p>
                </>
              )}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
